import {
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Directive,
  ViewContainerRef,
  TemplateRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ErrorSinkService, UiProgressPromiseService } from 'ngx-zcs-components';
import { ModbusLink } from 'ngx-modbus';

import { BatchWriterService, WriteRequest } from '../services/batch-writer.service';
import {
  ConfigFormSchema,
  ModeSection,
  FieldDefinition,
  SelectOption,
  Preset,
  ConfigFormSaveEvent,
  ConfigFormErrorEvent,
  VisibilityCondition,
  CrossFieldTransformFn,
  DynamicOptionsFn,
  CustomTriggerHandlerFn,
  CustomSectionContext,
} from '../types/config-form.types';
import { applyReadTransform, registerToBitToggle, registerToBitSelect } from '../transforms/field-transforms';
import { createDebugLogger, DebugConfig } from '../../shared/debug';

const CONFIG_FORM_DEBUG: DebugConfig = {
  ENABLED: true,
  LOG_PERFORMANCE: true,
  LOG_DATA: true,
  LOG_STATE: true,
};
const log = createDebugLogger('[ConfigForm]', CONFIG_FORM_DEBUG);

export const getConfigFormLabels = () => ({
  apply: 'Apply',
  saving: 'Saving...',
  trigger: 'Trigger',
  discard: 'Discard',
  save: 'Save',
  saveAll: 'Save All',
  presets: 'Presets',
  saveFailed: 'Save failed. Please try again.',
  loading: 'Loading...',
  read: 'Read',
  reading: 'Reading...',
  unsupported: 'Not supported by this device.',
});

@Directive()
export abstract class ConfigFormBase implements OnInit, OnDestroy {
  /** Pre-built schema */
  @Input() set schema(value: ConfigFormSchema | null | undefined) {
    if (value) {
      this.config = value;
      if (this._rawData && Object.keys(this._rawData).length > 0) {
        log.data('schema setter: config set, rawData exists — calling initializeFormValues', {
          schemaId: value.id,
          rawDataKeys: Object.keys(this._rawData).length,
        });
        this.initializeFormValues(this._rawData);
      } else {
        log.data('schema setter: config set, waiting for initialData', { schemaId: value.id });
      }
      this.cdr.markForCheck();
    }
  }

  /** Initial data from Modbus read */
  @Input() set initialData(data: Record<string, any> | null | undefined) {
    if (data) {
      log.data('initialData setter', {
        keys: Object.keys(data).length,
        hasConfig: !!this.config,
        configId: this.config?.id,
      });
      this._rawData = data;
      this.initializeFormValues(data);
    }
  }

  /** Register tables map for batch writes */
  @Input() registerTables: Record<string, any> = {};

  /** Modbus command instance (typed for safe API access) */
  @Input() modbusCommand?: ModbusLink;

  /** Custom cross-field transform functions (keyed by name) */
  @Input() customTransforms?: Record<string, CrossFieldTransformFn>;

  /** Custom dynamic option generator functions (keyed by name) */
  @Input() customOptionGenerators?: Record<string, DynamicOptionsFn>;

  /** Custom trigger handler functions (keyed by handler name from field definition) */
  @Input() customTriggerHandlers?: Record<string, CustomTriggerHandlerFn>;

  /** Custom section templates (keyed by customContent name from section definition) */
  @Input() customSectionTemplates?: Record<string, TemplateRef<any>>;

  /** Customizable button labels (host passes translated values) */
  _labels = getConfigFormLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels { return this._labels; }

  /** Emitted when save is requested */
  @Output() save = new EventEmitter<ConfigFormSaveEvent>();

  /** Emitted when a save operation fails */
  @Output() saveError = new EventEmitter<ConfigFormErrorEvent>();

  /** Emitted when preset is applied */
  @Output() presetApplied = new EventEmitter<{ presetId: string; values: Record<string, any> }>();

  /** Emitted when a read (re-read from device) is requested */
  @Output() read = new EventEmitter<void>();

  /** Emitted when all sections are hidden (e.g., all registers invalid by validity mask).
   *  Allows the host to hide the accordion or show a "not supported" indicator. */
  @Output() empty = new EventEmitter<void>();

  /** Optional progress container for showing spinner during saves */
  @Input() progressContainer?: ViewContainerRef;

  config: ConfigFormSchema | null = null;
  formValues: Record<string, any> = {};
  originalValues: Record<string, any> = {};
  currentMode: number | null = null;
  isLoading = false;
  /** Global flag — blocks all sections while any save is in progress (Modbus writes are sequential) */
  isSaving = false;
  isReading = false;
  /** True when at least one section has visible fields (validity mask check passed) */
  hasVisibleContent = true;
  /** True while an immediate mode change is being applied and waiting for data refresh */
  isModeApplying = false;
  errors: Record<string, string> = {};
  appliedPresetId: string | null = null;

  /** Last save error for display in template */
  lastSaveError: ConfigFormErrorEvent | null = null;

  protected _rawData: Record<string, any> = {};
  private _fieldOptionsCache = new Map<string, { depsKey: string; options: SelectOption[] }>();
  private _pendingModeRefresh = false;
  private _sectionApplying = new Set<string>();
  private _pendingSectionRefresh = new Set<string>();
  private _readingTimeout?: ReturnType<typeof setTimeout>;
  private destroy$ = new Subject<void>();

  constructor(
    protected batchWriter: BatchWriterService,
    protected cdr: ChangeDetectorRef,
    protected errorSink: ErrorSinkService,
    protected uiProgressPromiseService: UiProgressPromiseService,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this._readingTimeout);
  }

  private initializeFormValues(data: Record<string, any>): void {
    if (!this.config) return;
    this.errors = {};
    this.appliedPresetId = null;
    this._fieldOptionsCache.clear();

    log.data('initialData received', {
      keys: Object.keys(data).length,
      currentMode: this.config.modeSelector ? data[this.config.modeSelector.registerKey] : undefined,
      sectionSelectors: this.config.sections
        .filter(s => s.sectionSelector)
        .map(s => ({ section: s.id, key: s.sectionSelector!.registerKey, value: data[s.sectionSelector!.registerKey] })),
    });

    // Trace field resolution for debugging — show transform details for first few fields
    const fieldTrace: Record<string, any> = {};
    for (const section of this.config.sections) {
      for (const field of section.fields) {
        const raw = data[field.registerKey];
        const entry: any = { registerKey: field.registerKey, rawValue: raw, present: field.registerKey in data };
        if (field.transform) {
          entry.transform = field.transform;
          entry.transformFactor = field.transformFactor;
        }
        if (field.type === 'bit-toggle' || field.type === 'bit-select') {
          entry.type = field.type;
          entry.bitRegisterKey = field.bitRegisterKey;
          entry.parentRaw = data[field.bitRegisterKey ?? field.registerKey];
        }
        fieldTrace[field.key] = entry;
      }
    }
    log.data('field resolution trace', fieldTrace);

    // Initialize mode selector
    if (this.config.modeSelector) {
      const modeKey = this.config.modeSelector.registerKey;
      this.currentMode = data[modeKey] as number ?? null;
      this.formValues[this.config.modeSelector.key] = this.currentMode;
      this.originalValues[this.config.modeSelector.key] = this.currentMode;
    }

    // Initialize all section fields
    for (const section of this.config.sections) {
      // Initialize sectionSelector value
      if (section.sectionSelector) {
        const sel = section.sectionSelector;
        const selValue = data[sel.registerKey];
        this.formValues[sel.key] = selValue ?? null;
        this.originalValues[sel.key] = selValue ?? null;
      }

      for (const field of section.fields) {
        let value: any;

        // Bit fields: extract bit(s) from parent register
        if (field.type === 'bit-toggle' && field.bitIndex !== undefined) {
          const parentKey = field.bitRegisterKey ?? field.registerKey;
          value = registerToBitToggle(data[parentKey] as number, field.bitIndex);
        } else if (field.type === 'bit-select' && field.bitRange) {
          const parentKey = field.bitRegisterKey ?? field.registerKey;
          value = registerToBitSelect(data[parentKey] as number, field.bitRange[0], field.bitRange[1]);
        } else {
          const rawValue = data[field.registerKey];
          value = applyReadTransform(rawValue, {
            type: field.transform ?? 'none',
            factor: field.transformFactor,
          });
        }

        // Apply cross-field read transform
        if (field.crossFieldTransform && this.customTransforms?.[field.crossFieldTransform.name]) {
          const deps = this.getDependencyValues(field.crossFieldTransform.dependencies, data);
          value = this.customTransforms[field.crossFieldTransform.name](
            value, 'read', deps, field.crossFieldTransform.config,
          );
        }

        this.formValues[field.key] = value;
        this.originalValues[field.key] = value;
      }
    }
    // Clear pending mode refresh state (sections become unblocked)
    if (this._pendingModeRefresh) {
      this._pendingModeRefresh = false;
      this.isModeApplying = false;
    }

    // Clear pending section refresh state
    if (this._pendingSectionRefresh.size > 0) {
      this._pendingSectionRefresh.clear();
      this._sectionApplying.clear();
    }

    // Clear reading state when new data arrives
    if (this.isReading) {
      this.isReading = false;
      clearTimeout(this._readingTimeout);
    }

    // Check if the form has any visible content (mode selector OR visible sections)
    this.hasVisibleContent = this.isModeSelectorVisible()
      || this.config.sections.some(s => this.isSectionVisible(s));
    if (!this.hasVisibleContent) {
      this.empty.emit();
    }

    this.cdr.markForCheck();
  }

  // ============ Field Visibility ============

  /** Hide mode selector when validity mask marks its register as unsupported */
  isModeSelectorVisible(): boolean {
    if (!this.config?.modeSelector) {
      return false;
    }
    return this.modbusCommand?.validity?.[this.config.modeSelector.registerKey] !== false;
  }

  isFieldVisible(field: FieldDefinition): boolean {
    // Check hardware: device supports this register?
    if (this.modbusCommand?.validity?.[field.registerKey] === false) {
      return false;
    }
    if (!field.visibleWhen) return true;
    return this.evaluateCondition(field.visibleWhen);
  }

  isSectionVisible(section: ModeSection): boolean {
    if (section.modeValue !== undefined && this.currentMode !== section.modeValue) return false;
    if (section.customContent) return true;
    // Hide section if all fields are invisible (e.g., all excluded by validity mask)
    return section.fields.some((f) => this.isFieldVisible(f));
  }

  /** True when at least one section is visible (used to show global Read when no section has its own). */
  hasVisibleSections(): boolean {
    if (!this.config) return false;
    return this.config.sections.some((s) => this.isSectionVisible(s));
  }

  private evaluateCondition(condition: VisibilityCondition): boolean {
    const rawValue = this.formValues[condition.field] ?? this.currentMode;
    // Normalize booleans to numbers so bit-toggle (true/false) matches numeric conditions (1/0)
    const fieldValue = typeof rawValue === 'boolean' ? (rawValue ? 1 : 0) : rawValue;
    const operator = condition.operator ?? 'eq';

    if (Array.isArray(condition.value)) {
      return operator === 'in'
        ? condition.value.includes(fieldValue)
        : condition.value.some((v) => v === fieldValue);
    }

    return operator === 'neq'
      ? fieldValue !== condition.value
      : fieldValue === condition.value;
  }

  // ============ Dynamic Options ============

  getFieldOptions(field: FieldDefinition): SelectOption[] {
    if (field.dynamicOptions && this.customOptionGenerators?.[field.dynamicOptions.name]) {
      const deps = this.getDependencyValues(field.dynamicOptions.dependencies);
      const depsKey = JSON.stringify([this.formValues[field.key], deps, field.dynamicOptions.config]);
      const cached = this._fieldOptionsCache.get(field.key);
      if (cached && cached.depsKey === depsKey) {
        return cached.options;
      }
      const options = this.customOptionGenerators[field.dynamicOptions.name](
        this.formValues[field.key], deps, field.dynamicOptions.config,
      );
      this._fieldOptionsCache.set(field.key, { depsKey, options });
      return options;
    }
    return field.constraint?.options ?? [];
  }

  trackByOptionValue(_index: number, option: SelectOption): any {
    return option.value;
  }

  private getDependencyValues(dependencies: string[], rawData?: Record<string, any>): Record<string, any> {
    const deps: Record<string, any> = {};
    for (const key of dependencies) {
      deps[key] = rawData ? rawData[key] : this.formValues[key];
    }
    return deps;
  }

  // ============ Form State ============

  get isDirty(): boolean {
    return Object.keys(this.formValues).some(
      (key) => !this.isEqual(this.formValues[key], this.originalValues[key])
    );
  }

  isSectionDirty(section: ModeSection): boolean {
    if (section.customContent) return false;
    // Include sectionSelector in dirty check when it's not immediate (manual selectors are saved with the section)
    if (section.sectionSelector && section.sectionSelector.applyMode !== 'immediate') {
      if (this.isSectionSelectorDirty(section)) return true;
    }
    return section.fields.some(
      (field) => field.type !== 'trigger' && !this.isEqual(this.formValues[field.key], this.originalValues[field.key])
    );
  }

  /** Check if mode selector value has changed from original */
  isModeSelectorDirty(): boolean {
    if (!this.config?.modeSelector) return false;
    return this.currentMode !== this.originalValues[this.config.modeSelector.key];
  }

  /** Check if mode selector will be saved together with this section */
  willSaveModeWithSection(section: ModeSection): boolean {
    return this.isModeSelectorDirty() && !section.excludeModeSelectorOnSave;
  }

  get isValid(): boolean {
    return Object.keys(this.errors).length === 0;
  }

  /** True when any blocking operation is in progress (save, read, or immediate mode apply) */
  get isBusy(): boolean {
    return this.isSaving || this.isReading || this.isModeApplying;
  }

  /** True when the form has a single section and no mode selector — the host accordion already provides the title */
  get isSingleSection(): boolean {
    return !!this.config && this.config.sections.length === 1 && !this.config.modeSelector;
  }


  private isEqual(a: any, b: any): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
      return a.length === b.length && a.every((v, i) => v === b[i]);
    }
    return a === b;
  }

  // ============ Field Updates ============

  onFieldChange(field: FieldDefinition, value: any): void {
    this.formValues[field.key] = value;
    this.appliedPresetId = null;
    this.validateField(field, value);
    this.cdr.markForCheck();
  }

  onModeChange(value: number): void {
    this.currentMode = value;
    if (this.config?.modeSelector) {
      this.formValues[this.config.modeSelector.key] = value;
    }
    this.cdr.markForCheck();

    // Auto-apply when applyMode is 'immediate'
    if (this.config?.modeSelector?.applyMode === 'immediate' && this.isModeSelectorDirty()) {
      this.applyModeImmediate();
    }
  }

  // ============ Section Selector ============

  /** True if a section selector apply is in progress for this section */
  isSectionApplying(section: ModeSection): boolean {
    return this._sectionApplying.has(section.id);
  }

  /** True if this specific section is blocked (global busy OR section-level apply in progress) */
  isSectionBusy(section: ModeSection): boolean {
    return this.isBusy || this._sectionApplying.has(section.id);
  }

  /** Check if the section selector value has changed from original */
  isSectionSelectorDirty(section: ModeSection): boolean {
    if (!section.sectionSelector) return false;
    return !this.isEqual(
      this.formValues[section.sectionSelector.key],
      this.originalValues[section.sectionSelector.key],
    );
  }

  onSectionSelectorChange(section: ModeSection, value: any): void {
    if (!section.sectionSelector) return;
    this.formValues[section.sectionSelector.key] = value;
    this.cdr.markForCheck();

    // Auto-apply when applyMode is 'immediate' and value changed
    if (section.sectionSelector.applyMode === 'immediate' && this.isSectionSelectorDirty(section)) {
      this.applySectionSelectorImmediate(section);
    }
  }

  /** Auto-save sectionSelector and block section until data refresh arrives */
  private async applySectionSelectorImmediate(section: ModeSection): Promise<void> {
    const sel = section.sectionSelector;
    if (!sel || !this.modbusCommand) return;

    const selectorTimer = log.startTimer(`applySectionSelector("${section.id}", ${sel.registerKey}=${this.formValues[sel.key ?? sel.registerKey]})`);
    this._sectionApplying.add(section.id);
    this._pendingSectionRefresh.add(section.id);
    this.clearSaveError();
    this.cdr.markForCheck();

    const selectorTable = this.registerTables[sel.registerTable];
    const value = this.formValues[sel.key];

    const result = await this.modbusCommand!.executeMixedRequestsSafe([
      { mode: 'write', key: sel.registerKey, value, table: selectorTable },
    ]);

    if (result.isErr()) {
      log.error(`section selector apply failed for "${section.id}"`, result.error);
      this._sectionApplying.delete(section.id);
      this._pendingSectionRefresh.delete(section.id);
      this.emitSaveError({
        type: 'save-section',
        sectionId: section.id,
        message: 'Failed to apply section selector',
        error: result.error,
      });
    } else {
      this.originalValues[sel.key] = value;
      log.endTimer(selectorTimer, { sectionId: section.id, registerKey: sel.registerKey, value });
      this.save.emit({
        sectionId: section.id,
        values: { [sel.key]: value },
        transformedValues: { [sel.registerKey]: value },
        sectionSelectorSaved: true,
      });
    }
    this.cdr.markForCheck();
  }

  /** Auto-save modeSelector and block sections until data refresh arrives */
  private async applyModeImmediate(): Promise<void> {
    if (!this.config?.modeSelector || !this.modbusCommand) return;

    const modeTimer = log.startTimer(`applyModeImmediate(${this.originalValues[this.config.modeSelector.key]} → ${this.currentMode})`);
    this.isModeApplying = true;
    this._pendingModeRefresh = true;
    this.clearSaveError();
    this.cdr.markForCheck();

    try {
      await this._saveModeSelectorInternal();

      log.endTimer(modeTimer, { mode: this.currentMode });

      this.save.emit({
        sectionId: '__modeSelector__',
        values: { [this.config.modeSelector.key]: this.currentMode },
        transformedValues: { [this.config.modeSelector.registerKey]: this.currentMode as number },
        modeSelectorSaved: true,
      });
      // isModeApplying stays true until initializeFormValues clears _pendingModeRefresh
    } catch (error: any) {
      log.error('immediate mode apply failed', error);
      this.isModeApplying = false;
      this._pendingModeRefresh = false;
      const errorEvent: ConfigFormErrorEvent = {
        type: 'save-mode',
        message: error?.message || 'Failed to apply mode change',
        error,
      };
      this.emitSaveError(errorEvent);
    }
    this.cdr.markForCheck();
  }

  private validateField(field: FieldDefinition, value: any): void {
    delete this.errors[field.key];

    if (field.constraint) {
      if (field.constraint.min !== undefined && value < field.constraint.min) {
        this.errors[field.key] = `Min: ${field.constraint.min}`;
      }
      if (field.constraint.max !== undefined && value > field.constraint.max) {
        this.errors[field.key] = `Max: ${field.constraint.max}`;
      }
    }
  }

  // ============ Time/Date Helpers ============

  // Date value is stored as [month, day]
  getDateMonth(field: FieldDefinition): number | string {
    const value = this.formValues[field.key];
    if (Array.isArray(value) && value.length === 2) {
      return value[0];
    }
    return '';
  }

  getDateDay(field: FieldDefinition): number | string {
    const value = this.formValues[field.key];
    if (Array.isArray(value) && value.length === 2) {
      return value[1];
    }
    return '';
  }

  setDateMonth(field: FieldDefinition, monthValue: number | string): void {
    const month = typeof monthValue === 'number' ? monthValue : parseInt(monthValue, 10);
    if (isNaN(month)) return;
    const currentValue = this.formValues[field.key];
    const day = Array.isArray(currentValue) ? currentValue[1] : 1;
    this.onFieldChange(field, [month, day]);
  }

  setDateDay(field: FieldDefinition, dayValue: number | string): void {
    const day = typeof dayValue === 'number' ? dayValue : parseInt(dayValue, 10);
    if (isNaN(day)) return;
    const currentValue = this.formValues[field.key];
    const month = Array.isArray(currentValue) ? currentValue[0] : 1;
    this.onFieldChange(field, [month, day]);
  }

  getWeekdayValue(field: FieldDefinition): string[] {
    const value = this.formValues[field.key];
    return Array.isArray(value) ? value : [];
  }

  setWeekdayValue(field: FieldDefinition, days: string[]): void {
    this.onFieldChange(field, days);
  }

  toggleWeekday(field: FieldDefinition, day: string, checked: boolean): void {
    const currentDays = this.getWeekdayValue(field);
    const newDays = checked
      ? [...currentDays, day]
      : currentDays.filter((d) => d !== day);
    this.setWeekdayValue(field, newDays);
  }

  readonly weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  readonly months = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' },
  ];

  readonly days = Array.from({ length: 31 }, (_, i) => i + 1);

  readonly hours = Array.from({ length: 24 }, (_, i) => i);   // 0-23
  readonly minutes = Array.from({ length: 60 }, (_, i) => i); // 0-59

  getTimeHour(field: FieldDefinition): number | string {
    const value = this.formValues[field.key];
    if (Array.isArray(value) && value.length === 2) return value[0];
    return '';
  }

  getTimeMinute(field: FieldDefinition): number | string {
    const value = this.formValues[field.key];
    if (Array.isArray(value) && value.length === 2) return value[1];
    return '';
  }

  setTimeHour(field: FieldDefinition, hourValue: number | string): void {
    const hour = typeof hourValue === 'number' ? hourValue : parseInt(hourValue, 10);
    if (isNaN(hour)) return;
    const currentValue = this.formValues[field.key];
    const minute = Array.isArray(currentValue) ? currentValue[1] : 0;
    this.onFieldChange(field, [hour, minute]);
  }

  setTimeMinute(field: FieldDefinition, minuteValue: number | string): void {
    const minute = typeof minuteValue === 'number' ? minuteValue : parseInt(minuteValue, 10);
    if (isNaN(minute)) return;
    const currentValue = this.formValues[field.key];
    const hour = Array.isArray(currentValue) ? currentValue[0] : 0;
    this.onFieldChange(field, [hour, minute]);
  }

  // ============ Presets ============

  getAvailablePresets(): Preset[] {
    if (!this.config?.presets) return [];
    return this.config.presets.filter((preset) => {
      if (!preset.forModes || preset.forModes.length === 0) return true;
      return this.currentMode !== null && preset.forModes.includes(this.currentMode);
    });
  }

  applyPreset(preset: Preset): void {
    for (const [key, value] of Object.entries(preset.values)) {
      this.formValues[key] = value;
    }
    this.appliedPresetId = preset.id;
    this.presetApplied.emit({ presetId: preset.id, values: preset.values });
    this.cdr.markForCheck();
  }

  // ============ Save ============

  /** Save all visible fields of a section (not just dirty ones) to ensure a contiguous Modbus batch write */
  async saveSection(section: ModeSection): Promise<void> {
    if (!this.modbusCommand || !this.isSectionDirty(section)) return;

    const saveTimer = log.startTimer(`saveSection("${section.id}")`);
    this.isSaving = true;
    this.clearSaveError(); // Clear previous error
    this.cdr.markForCheck();

    let modeSelectorSaved = false;
    let progress: { hide: () => void; update: (percentage: number) => void } | undefined;

    if (this.progressContainer) {
      progress = await this.uiProgressPromiseService.progress(this.progressContainer);
    }

    try {
      // Check if mode selector should be saved with this section
      const shouldSaveModeSelector =
        this.isModeSelectorDirty() && !section.excludeModeSelectorOnSave;

      if (shouldSaveModeSelector) {
        await this._saveModeSelectorInternal();
        modeSelectorSaved = true;
      }

      const visibleFields = section.fields.filter((f) => this.isFieldVisible(f) && f.type !== 'trigger');

      // Build a copy of formValues with cross-field write transforms applied
      const writeValues = { ...this.formValues };
      for (const field of visibleFields) {
        if (field.crossFieldTransform && this.customTransforms?.[field.crossFieldTransform.name]) {
          const deps = this.getDependencyValues(field.crossFieldTransform.dependencies);
          writeValues[field.key] = this.customTransforms[field.crossFieldTransform.name](
            writeValues[field.key], 'write', deps, field.crossFieldTransform.config,
          );
        }
      }

      const registerTable = this.registerTables[section.registerTable];
      const requests = this.batchWriter.buildWriteRequests({
        section,
        values: writeValues,
        fields: visibleFields,
        command: this.modbusCommand,
        registerTable,
      });

      // Prepend modeSelector register if alwaysIncludeInSectionWrites is set
      // This ensures the write starts from the modeSelector address (e.g., BAT_CONFIG_ID @ 0x1044)
      if (this.config?.modeSelector?.alwaysIncludeInSectionWrites && this.currentMode !== null) {
        const modeSelectorKey = this.config.modeSelector.registerKey;
        // Only prepend if not already in the requests
        if (!requests.some((r) => r.key === modeSelectorKey)) {
          requests.unshift({ key: modeSelectorKey, value: this.currentMode });
        }
      }

      // Prepend sectionSelector register if alwaysIncludeInSectionWrites is set
      if (section.sectionSelector?.alwaysIncludeInSectionWrites) {
        const selKey = section.sectionSelector.registerKey;
        const selValue = this.formValues[section.sectionSelector.key];
        if (selValue !== null && selValue !== undefined && !requests.some((r) => r.key === selKey)) {
          requests.unshift({ key: selKey, value: selValue });
        }
      }

      if (!registerTable) {
        const errorEvent: ConfigFormErrorEvent = {
          type: 'save-section',
          sectionId: section.id,
          message: `Register table not found: ${section.registerTable}`,
        };
        this.emitSaveError(errorEvent);
        return;
      }

      const writeResult = await this.batchWriter.executeBatchWrite(
        this.modbusCommand,
        requests,
        registerTable,
        {
          ...(section.validityMask ? { useValidityMask: section.validityMask } : {}),
          ...(section.controlRegister ? { controlRegister: section.controlRegister } : {}),
          ...(section.writeBlockSize ? { writeBlockSize: section.writeBlockSize } : {}),
          updateProgress: progress?.update,
        },
      );

      if (writeResult.isErr()) {
        this.emitSaveError({
          type: 'save-section',
          sectionId: section.id,
          message: writeResult.error.kind === 'transport'
            ? 'Communication error' : 'Failed to save settings',
          error: writeResult.error,
        });
      } else {
        log.endTimer(saveTimer, {
          visibleFields: visibleFields.length,
          requests: requests.length,
          modeSelectorSaved,
        });

        for (const field of visibleFields) {
          this.originalValues[field.key] = this.formValues[field.key];
        }

        this.save.emit({
          sectionId: section.id,
          values: this.formValues,
          transformedValues: requests.reduce((acc, r) => ({ ...acc, [r.key]: r.value }), {}),
          controlRegister: section.controlRegister,
          modeSelectorSaved,
        });
      }
    } catch (error: any) {
      console.error('[ConfigForm] Save failed for section', section.id, '- error:', error);
      const errorEvent: ConfigFormErrorEvent = {
        type: 'save-section',
        sectionId: section.id,
        message: error?.message || 'Failed to save settings',
        error,
      };
      this.emitSaveError(errorEvent);
    } finally {
      progress?.hide();
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  }

  async triggerAction(field: FieldDefinition, section: ModeSection): Promise<void> {
    if (!this.modbusCommand || this.isSaving) return;

    // Custom trigger handler — delegate entirely to host-provided function
    if (field.customTriggerHandler && this.customTriggerHandlers?.[field.customTriggerHandler]) {
      this.isSaving = true;
      this.clearSaveError();
      this.cdr.markForCheck();
      try {
        await this.customTriggerHandlers[field.customTriggerHandler]({
          command: this.modbusCommand,
          registerTables: this.registerTables,
          field,
          section,
        });
        this.save.emit({
          sectionId: section.id,
          values: { [field.key]: 'custom-trigger' },
          transformedValues: {},
        });
      } catch (error: any) {
        this.emitSaveError({
          type: 'save-section',
          sectionId: section.id,
          message: error?.message || 'Trigger action failed',
          error,
        });
      } finally {
        this.isSaving = false;
        this.cdr.markForCheck();
      }
      return;
    }

    const triggerValue = field.triggerValue ?? 1;
    console.log(`[ConfigForm] triggerAction("${section.id}", "${field.key}", value=${triggerValue})`);
    this.isSaving = true;
    this.clearSaveError();
    this.cdr.markForCheck();

    try {
      const requests: WriteRequest[] = [{ key: field.registerKey, value: triggerValue }];

      if (section.controlRegister) {
        requests.push({ key: section.controlRegister, value: 1 });
      }

      const registerTable = this.registerTables[section.registerTable];
      if (!registerTable) {
        this.emitSaveError({
          type: 'save-section', sectionId: section.id,
          message: `Register table not found: ${section.registerTable}`,
        });
        return;
      }

      const triggerResult = await this.batchWriter.executeBatchWrite(
        this.modbusCommand, requests, registerTable, {
          ...(section.validityMask ? { useValidityMask: section.validityMask } : {}),
          ...(section.controlRegister ? { controlRegister: section.controlRegister } : {}),
        },
      );

      if (triggerResult.isErr()) {
        this.emitSaveError({
          type: 'save-section', sectionId: section.id,
          message: 'Trigger action failed',
          error: triggerResult.error,
        });
      } else {
        this.save.emit({
          sectionId: section.id,
          values: { [field.key]: triggerValue },
          transformedValues: { [field.registerKey]: triggerValue },
        });
      }
    } catch (error: any) {
      console.error('[ConfigForm] Trigger failed for', field.key, error);
      this.emitSaveError({
        type: 'save-section', sectionId: section.id,
        message: error?.message || 'Trigger action failed',
        error,
      });
    } finally {
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  }

  async saveModeSelector(): Promise<void> {
    if (!this.config?.modeSelector || !this.modbusCommand) return;
    if (!this.isModeSelectorDirty()) return;

    this.isSaving = true;
    this.clearSaveError();
    this.cdr.markForCheck();

    try {
      await this._saveModeSelectorInternal();

      // Emit save event so host can trigger data refresh (e.g., after changing BAT_CONFIG_ID)
      this.save.emit({
        sectionId: '__modeSelector__',
        values: { [this.config.modeSelector.key]: this.currentMode },
        transformedValues: { [this.config.modeSelector.registerKey]: this.currentMode as number },
        modeSelectorSaved: true,
      });
    } catch (error: any) {
      console.error('[ConfigForm] Mode save failed:', error);
      const errorEvent: ConfigFormErrorEvent = {
        type: 'save-mode',
        message: error?.message || 'Failed to save mode',
        error,
      };
      this.emitSaveError(errorEvent);
    } finally {
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  }

  /** Internal method to save mode selector without managing isSaving state */
  private async _saveModeSelectorInternal(): Promise<void> {
    if (!this.config?.modeSelector || !this.modbusCommand) return;

    const registerTable = this.registerTables[this.config.modeSelector.registerTable];
    const result = await this.modbusCommand!.executeMixedRequestsSafe([
      {
        mode: 'write',
        key: this.config.modeSelector.registerKey,
        value: this.currentMode as number,
        table: registerTable,
      },
    ]);

    if (result.isErr()) {
      throw result.error; // Re-throw as DeviceError to let caller handle
    }

    this.originalValues[this.config.modeSelector.key] = this.currentMode;
  }

  // ============ Error Handling ============

  /** Report error to ErrorSink + emit event for parent + set internal state */
  private emitSaveError(errorEvent: ConfigFormErrorEvent): void {
    this.lastSaveError = errorEvent;
    this.saveError.emit(errorEvent);

    // Report directly to ErrorSink — parent no longer needs to relay to ErrorSink
    const error = errorEvent.error;
    this.errorSink.report({
      code: `SAVE_${errorEvent.type}_${errorEvent.sectionId ?? 'unknown'}`,
      message: errorEvent.message,
      severity: 'toast',
      source: error?.kind === 'transport' ? 'transport' : 'application',
      technicalDetail: `[ConfigForm] ${errorEvent.type}: ${errorEvent.sectionId ?? ''} ${error?.kind ?? ''}`,
      timestamp: Date.now(),
    });
    this.cdr.markForCheck();
  }

  /** Clear the last save error */
  clearSaveError(): void {
    this.lastSaveError = null;
    this.cdr.markForCheck();
  }

  /** Build context object for custom section templates */
  getCustomSectionContext(section: ModeSection): CustomSectionContext {
    return {
      section,
      initialData: this._rawData,
      command: this.modbusCommand,
      registerTables: this.registerTables,
      isSaving: this.isSaving,
      labels: this._labels,
    };
  }

  // ============ Read ============

  requestRead(): void {
    this.isReading = true;
    this.clearSaveError();
    this.cdr.markForCheck();
    this.read.emit();
    // Safety timeout — clear reading state after 30s if host never responds
    clearTimeout(this._readingTimeout);
    this._readingTimeout = setTimeout(() => {
      if (this.isReading) {
        this.isReading = false;
        this.cdr.markForCheck();
      }
    }, 30_000);
  }

  // ============ Reset ============

  resetSection(section: ModeSection): void {
    if (section.sectionSelector) {
      this.formValues[section.sectionSelector.key] = this.originalValues[section.sectionSelector.key];
    }
    for (const field of section.fields) {
      this.formValues[field.key] = this.originalValues[field.key];
    }
    this.cdr.markForCheck();
  }

  resetAll(): void {
    this.formValues = { ...this.originalValues };
    if (this.config?.modeSelector) {
      this.currentMode = this.originalValues[this.config.modeSelector.key];
    }
    this.cdr.markForCheck();
  }
}
